import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Contexts and Components
import { useProject, PROJECT_STATUS } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { TableSkeleton, EmptyState, StatCardSkeleton, PageHeaderCard } from '../../components/common';

// Services
import { usersAPI } from '../../services/api';

// Utils and Constants
import { PROJECT_STATUS_FILTER_OPTIONS } from '../../constants/projectEnums';
import { getProjectStatusColor, getProjectStatusLabel, formatDateMMDDYYYY } from '../../utils/helper';

const SORT_FIELD_MAP = {
  name: 'name',
  status: 'status',
  manager: 'manager_name',
  dueDate: 'due_date',
};

const initialEditForm = {
  name: '',
  description: '',
  status: PROJECT_STATUS.NOT_STARTED,
  managerId: '',
  dueDate: '',
};

const getProjectStats = (projects) => {
  const total = projects.length;
  const inProgress = projects.filter((p) => p.status === PROJECT_STATUS.IN_PROGRESS).length;
  const completed = projects.filter((p) => p.status === PROJECT_STATUS.COMPLETED).length;
  const totalTasks = projects.reduce((sum, p) => sum + (p.tasks?.length || 0), 0);
  return { total, inProgress, completed, totalTasks };
};

const ProjectList = () => {
  const navigate = useNavigate();
  const { getActiveCompany, user, ROLES } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { getAllProjects, isProjectsLoading, projectsError, refreshProjects, updateProject } = useProject();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;
  const activeCompanyRole = (activeCompany?.role || '').toLowerCase();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSearch, setShowSearch] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: 'dueDate', direction: 'desc' });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [cardStats, setCardStats] = useState({ total: 0, inProgress: 0, completed: 0, totalTasks: 0 });
  const [isCardStatsInitialized, setIsCardStatsInitialized] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [formErrors, setFormErrors] = useState({});
  const [availableManagers, setAvailableManagers] = useState([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const allProjects = getAllProjects();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const sortBy = sortConfig.field === 'progress'
      ? undefined
      : (SORT_FIELD_MAP[sortConfig.field] || 'created_at');
    refreshProjects({
      statusFilter: statusFilter === 'all' ? undefined : statusFilter,
      search: debouncedSearchTerm || undefined,
      sortBy,
      sortOrder: sortConfig.direction,
      page: 1,
      pageSize: 100,
    });
  }, [refreshProjects, statusFilter, debouncedSearchTerm, sortConfig.field, sortConfig.direction]);

  useEffect(() => {
    setCardStats({ total: 0, inProgress: 0, completed: 0, totalTasks: 0 });
    setIsCardStatsInitialized(false);
  }, [activeCompanyId]);

  useEffect(() => {
    const fetchManagers = async () => {
      if (!activeCompanyId) {
        setAvailableManagers([]);
        return;
      }

      setIsLoadingManagers(true);
      try {
        const response = await usersAPI.listByCompany(activeCompanyId, {
          includeSelf: true,
          page: 1,
          pageSize: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        const users = response.items || [];
        setAvailableManagers(
          users.map((member) => ({
            id: member.id,
            name: member.name || member.email,
            email: member.email,
          }))
        );
      } catch (error) {
        setAvailableManagers([]);
      } finally {
        setIsLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [activeCompanyId]);

  // Freeze cards from first successful page load for current company.
  useEffect(() => {
    if (isProjectsLoading || isCardStatsInitialized) {
      return;
    }

    setCardStats(getProjectStats(allProjects));
    setIsCardStatsInitialized(true);
  }, [allProjects, isProjectsLoading, isCardStatsInitialized]);

  // Handle sort
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedProjects = useMemo(() => {
    if (sortConfig.field !== 'progress') {
      return allProjects;
    }

    const projectsCopy = [...allProjects];
    projectsCopy.sort((a, b) => {
      const aProgress = a.progress ?? 0;
      const bProgress = b.progress ?? 0;

      if (aProgress < bProgress) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aProgress > bProgress) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return projectsCopy;
  }, [allProjects, sortConfig.field, sortConfig.direction]);

  const filteredProjects = sortedProjects;
  const editableStatusOptions = PROJECT_STATUS_FILTER_OPTIONS.filter((option) => option.value !== 'all');

  const paginatedProjects = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProjects.slice(start, start + rowsPerPage);
  }, [filteredProjects, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const canEditProject = useCallback((project) => {
    if (user?.role === ROLES.ADMIN || activeCompanyRole === ROLES.COMPANY_ADMIN) {
      return true;
    }

    return Boolean(project?.createdById && user?.id && project.createdById === user.id);
  }, [activeCompanyRole, user?.id, user?.role, ROLES.ADMIN, ROLES.COMPANY_ADMIN]);

  const handleOpenEditDialog = (project) => {
    setSelectedProject(project);
    setEditForm({
      name: project.name || '',
      description: project.description || '',
      status: project.status || PROJECT_STATUS.NOT_STARTED,
      managerId: project.managerId || '',
      dueDate: project.dueDate || '',
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedProject(null);
    setEditForm(initialEditForm);
    setFormErrors({});
    setIsSubmittingEdit(false);
  };

  const validateEditForm = () => {
    const nextErrors = {};

    if (!editForm.name.trim()) {
      nextErrors.name = 'Project name is required';
    }
    if (!editForm.description.trim()) {
      nextErrors.description = 'Description is required';
    }
    if (!editForm.status) {
      nextErrors.status = 'Status is required';
    }
    if (!editForm.managerId) {
      nextErrors.managerId = 'Manager is required';
    }
    if (!editForm.dueDate) {
      nextErrors.dueDate = 'Due date is required';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!selectedProject) {
      return;
    }

    if (!validateEditForm()) {
      return;
    }

    setIsSubmittingEdit(true);
    try {
      await updateProject(selectedProject.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        status: editForm.status,
        managerId: editForm.managerId,
        dueDate: editForm.dueDate,
      });

      showSuccess('Project updated successfully');
      handleCloseEditDialog();
    } catch (error) {
      showError(error?.message || 'Failed to update project');
      setIsSubmittingEdit(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeaderCard
        title="All Projects"
        subtitle="Manage and track all your projects and tasks"
        titleVariant={isMobile ? 'h5' : 'h4'}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
            fullWidth={isMobile}
          >
            Create Project
          </Button>
        )}
      />

      {/* Statistics Cards */}
      {isProjectsLoading && !isCardStatsInitialized ? (
        <Box sx={{ mb: 3 }}>
          <StatCardSkeleton count={4} />
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          mb: 3
        }}>
          {[
            { title: 'Total Projects', value: cardStats.total, icon: FolderIcon, color: '#1976d2' },
            { title: 'In Progress', value: cardStats.inProgress, icon: HourglassEmptyIcon, color: '#2e7d32' },
            { title: 'Completed', value: cardStats.completed, icon: CheckCircleIcon, color: '#ed6c02' },
            { title: 'Total Tasks', value: cardStats.totalTasks, icon: AssignmentIcon, color: '#9c27b0' },
          ].map((item) => {
            const IconComponent = item.icon;
            return (
              <Card
                key={item.title}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  height: { xs: 100, sm: 120 },
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, lineHeight: 1.2 }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: `${item.color}14`,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconComponent sx={{ color: item.color, fontSize: { xs: 24, sm: 28 } }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {projectsError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {projectsError}
        </Alert>
      )}

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
          Filter
        </Typography>
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
          }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }} fullWidth={isMobile}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {PROJECT_STATUS_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(searchTerm || statusFilter !== 'all') && (
              <Chip
                label={`${filteredProjects.length} results`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Projects Table */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 8,
              height: 24,
              borderRadius: 1,
              backgroundColor: 'primary.main',
            }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Projects List
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setShowSearch(!showSearch)}
              sx={{
                color: showSearch ? 'primary.main' : 'text.secondary',
                '&:hover': { backgroundColor: 'primary.50' }
              }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {filteredProjects.length} projects
            </Typography>
          </Box>
        </Box>
        {/* Collapsible Search */}
        <Collapse in={showSearch}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            <TextField
              size="small"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Collapse>
        {isProjectsLoading ? (
          <TableSkeleton columns={isMobile ? 3 : isTablet ? 4 : 7} rows={5} dense={isMobile} />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 150 }}>
                    <TableSortLabel
                      active={sortConfig.field === 'name'}
                      direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('name')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: sortConfig.field === 'name' ? 1 : 0.3,
                        },
                        '&:hover .MuiTableSortLabel-icon': {
                          opacity: 1,
                        },
                      }}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  {!isTablet && (
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                      Description
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'status'}
                      direction={sortConfig.field === 'status' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('status')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: sortConfig.field === 'status' ? 1 : 0.3,
                        },
                        '&:hover .MuiTableSortLabel-icon': {
                          opacity: 1,
                        },
                      }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 120 }}>
                      <TableSortLabel
                        active={sortConfig.field === 'progress'}
                        direction={sortConfig.field === 'progress' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('progress')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: sortConfig.field === 'progress' ? 1 : 0.3,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        Progress
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {!isTablet && (
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                      <TableSortLabel
                        active={sortConfig.field === 'manager'}
                        direction={sortConfig.field === 'manager' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('manager')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: sortConfig.field === 'manager' ? 1 : 0.3,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        Manager
                      </TableSortLabel>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                      <TableSortLabel
                        active={sortConfig.field === 'dueDate'}
                        direction={sortConfig.field === 'dueDate' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('dueDate')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            opacity: sortConfig.field === 'dueDate' ? 1 : 0.3,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        Due Date
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : isTablet ? 4 : 7} sx={{ border: 0 }}>
                      <EmptyState
                        variant={searchTerm || statusFilter !== 'all' ? 'search' : 'projects'}
                        actionLabel={!(searchTerm || statusFilter !== 'all') ? 'Create Project' : undefined}
                        onAction={!(searchTerm || statusFilter !== 'all') ? handleCreateProject : undefined}
                      />
                      {(searchTerm || statusFilter !== 'all') && (
                        <Box sx={{ textAlign: 'center', mt: -2, mb: 2 }}>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              setSearchTerm('');
                              setStatusFilter('all');
                            }}
                          >
                            Clear filters
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProjects.map((project, index) => (
                    <TableRow
                      key={project.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                        },
                      }}
                      onClick={() => handleRowClick(project.id)}
                    >
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {!isMobile && (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: 'primary.50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FolderIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                            </Box>
                          )}
                          <Box>
                            <Typography fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                              {project.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {project.taskCount ?? 0} tasks
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!isTablet && (
                        <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                          <Tooltip title={project.description} arrow placement="top">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.85rem',
                              }}
                            >
                              {project.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                        <Chip
                          label={getProjectStatusLabel(project.status)}
                          color={getProjectStatusColor(project.status)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-label': { px: 1.5 }
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ minWidth: 120, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={project.progress}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  bgcolor: project.progress === 100 ? 'success.main' : 'primary.main',
                                },
                              }}
                            />
                            <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ minWidth: 35, fontSize: '0.75rem' }}>
                              {project.progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {!isTablet && (
                        <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {project.manager}
                          </Typography>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {formatDateMMDDYYYY(project.dueDate)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                        <Tooltip title="View" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(project.id);
                            }}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'primary.50' },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEditProject(project) && (
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(project);
                              }}
                              sx={{
                                color: 'primary.light',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={filteredProjects.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </Card>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon sx={{ color: 'inherit' }} />
            <Typography variant="h6" color="inherit">Edit Project</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Name"
              value={editForm.name}
              onChange={(e) => {
                setEditForm((prev) => ({ ...prev, name: e.target.value }));
                if (formErrors.name) {
                  setFormErrors((prev) => ({ ...prev, name: '' }));
                }
              }}
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => {
                setEditForm((prev) => ({ ...prev, description: e.target.value }));
                if (formErrors.description) {
                  setFormErrors((prev) => ({ ...prev, description: '' }));
                }
              }}
              error={Boolean(formErrors.description)}
              helperText={formErrors.description}
              fullWidth
              multiline
              minRows={3}
              required
            />

            <FormControl fullWidth required error={Boolean(formErrors.status)}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editForm.status}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, status: e.target.value }));
                  if (formErrors.status) {
                    setFormErrors((prev) => ({ ...prev, status: '' }));
                  }
                }}
              >
                {editableStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required error={Boolean(formErrors.managerId)}>
              <InputLabel>Manager</InputLabel>
              <Select
                label="Manager"
                value={editForm.managerId}
                onChange={(e) => {
                  setEditForm((prev) => ({ ...prev, managerId: e.target.value }));
                  if (formErrors.managerId) {
                    setFormErrors((prev) => ({ ...prev, managerId: '' }));
                  }
                }}
              >
                {isLoadingManagers ? (
                  <MenuItem disabled value="">
                    Loading managers...
                  </MenuItem>
                ) : (
                  availableManagers.map((manager) => (
                    <MenuItem key={manager.id} value={manager.id}>
                      {manager.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.managerId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.75, ml: 1.75 }}>
                  {formErrors.managerId}
                </Typography>
              )}
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Due Date"
                value={editForm.dueDate ? dayjs(editForm.dueDate) : null}
                format="DD MMM YYYY"
                onChange={(value) => {
                  setEditForm((prev) => ({
                    ...prev,
                    dueDate: value ? value.format('YYYY-MM-DD') : '',
                  }));
                  if (formErrors.dueDate) {
                    setFormErrors((prev) => ({ ...prev, dueDate: '' }));
                  }
                }}
                slotProps={{
                  textField: {
                    error: Boolean(formErrors.dueDate),
                    helperText: formErrors.dueDate,
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} variant="outlined" color="inherit" disabled={isSubmittingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
            {isSubmittingEdit ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectList;
